<template>
  <validation-observer
    v-slot="contentCalculatorObserver"
    disabled
    slim
  >
    <form-section
      first
      class="contents-calculator"
    >
      <form-section-highlight>
        <form-row :is-vertical-splitted="false">
          <div style="width: 100%;">
            <p class="text-center">
              Imagine your home was turned upside down - what would fall to the ceiling? It's all of those things you need to include.
            </p>
            <v-row class="text-left">
              <contents-calculator-help-text-col
                v-for="item in mainContentsCalcPoints"
                :key="item.title"
                :title="item.title"
                :list="item.list"
              />
              <v-col md="4" />
            </v-row>
            <p
              class="text-center font-weight-medium py-4"
              style="font-size: 18px;"
            >
              What else should I include or exclude in my contents?
            </p>
            <v-row class="text-left">
              <contents-calculator-help-text-col
                v-for="item in additionalContentsCalcPoints"
                :key="item.title"
                :title="item.title"
                :list="item.list"
              />
            </v-row>

            <validation-provider
              v-for="field in contentsFields"
              :key="concatFirstToLowerCase(field.name)"
              v-slot="{ errors, failed, valid, dirty }"
              :name="field.name"
              :vid="concatFirstToLowerCase(field.name)"
              rules="contentsCalcItemValue"
              slim
            >
              <form-section
                :validated="isValidated"
                :valid="valid"
                :dirty="dirty"
              >
                <form-row
                  :label="field.title"
                  class="mb-0"
                >
                  <qz-number-input
                    v-model.number="field.value"
                    :data-test="`contents_calc_${snakeCase(field.name)}`"
                    :maxlength="inputCharsLimiters.INSURED_ITEM_MAX_VALUE"
                    :error="failed"
                    :error-messages="errors"
                    prefix="£"
                  />
                </form-row>
              </form-section>
            </validation-provider>

            <form-row class="py-8">
              <p
                slot="label"
                class="text-left"
              >
                Your total calculated amount is based on the estimated value of your contents - please check it would be enough to replace all your belongings.
              </p>

              <validation-provider
                v-slot="{ errors, failed }"
                name="Sum All Rooms Contents Ins"
                vid="sumAllRoomsContentsIns"
                :rules="{ contentsReplaceCost: {
                  totalContentsCost: sumAllRoomsContentsIns,
                  isContentsCalc: true
                } }"
                tag="div"
              >
                <qz-text-field
                  :value="sumAllRoomsContentsIns"
                  readonly
                  data-test="sum_all_rooms_contents_ins"
                  :error="isValidated && failed"
                  :error-messages="errors"
                  prefix="£"
                />
                <qz-btn
                  depressed
                  x-large
                  outlined
                  rounded
                  color="primary"
                  data-test="use_calculated_contents_sums_ins"
                  class="mt-8"
                  @click="setContentsSumsIns(contentCalculatorObserver)"
                >
                  Use calculated amount
                </qz-btn>
              </validation-provider>
            </form-row>

            <p>
              <qz-btn
                text
                color="primary"
                data-test="close_calc"
                @click="closeContentsCalculator"
              >
                <span class="text-decoration-underline">Close Calculator</span>
                <v-icon right>
                  mdi-chevron-up
                </v-icon>
              </qz-btn>
            </p>
          </div>
        </form-row>
      </form-section-highlight>
    </form-section>
  </validation-observer>
</template>

<script>
import { snakeCase } from 'lodash';
import ContentsCalculatorHelpTextCol
from '@/components/home/ContentsCalculator/ContentsCalculatorHelpTextCol';

export default {
  name: 'contents-calculator',
  components: { ContentsCalculatorHelpTextCol },
  inject: [
    'inputCharsLimiters',
  ],
  data () {
    return {
      contentsFields: [
        {
          value: 0,
          title: 'Hallways, storage spaces, loft and basement',
          name: 'Hallways Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Bedrooms',
          name: 'Bedrooms Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Bathrooms',
          name: 'Bathrooms Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Reception rooms',
          name: 'Reception Rooms Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Dining room',
          name: 'Dining Room Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Kitchen',
          name: 'Kitchen Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Utility rooms',
          name: 'Utility Rooms Contents Sums Ins',
        },
        {
          value: 0,
          title: 'Garden, garages and outbuildings',
          name: 'Outbuildings Contents Sums Ins',
        },
      ],
      isValidated: false,
    };
  },
  computed: {
    sumAllRoomsContentsIns () {
      return this.contentsFields.reduce((acc, current) => {
        return acc + +current.value;
      }, 0);
    },
  },
  methods: {
    concatFirstToLowerCase (val) {
      const concatenated = val.replace(/ /g, '');
      return concatenated.charAt(0) + concatenated.slice(1);
    },
    async setContentsSumsIns (contentCalculatorObserver) {
      this.isValidated = true;
      const isValid = await contentCalculatorObserver.validate();

      if (!isValid) return;

      this.$emit('summarized', this.sumAllRoomsContentsIns);
    },
    closeContentsCalculator () {
      this.$emit('close-calculator');
    },
  },
  setup () {
    const mainContentsCalcPoints = [
      {
        title: 'Typically include',
        list: [
          {
            label: 'Carpets and curtains',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Freestanding furniture',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'TVs and tech gadgets',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Jewellery and ornaments',
            color: 'success',
            icon: 'mdi-check',
          },
        ],
      },
      {
        title: 'Typically exclude',
        list: [
          {
            label: 'Laminate Flooring',
            color: 'error',
            icon: 'mdi-window-close',
          },
          {
            label: 'Fitted units and cupboards',
            color: 'error',
            icon: 'mdi-window-close',
          },
          {
            label: 'Fixed mirrors',
            color: 'error',
            icon: 'mdi-window-close',
          },
          {
            label: 'Light fittings',
            color: 'error',
            icon: 'mdi-window-close',
          },
        ],
      },
    ];
    const additionalContentsCalcPoints = [
      {
        title: 'Hallways and storage spaces',
        list: [
          {
            label: 'Carpets and curtains',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Freestanding furniture',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Ornaments',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Freestanding lighting',
            color: 'success',
            icon: 'mdi-check',
          },
        ],
      },
      {
        title: 'Bedrooms',
        list: [
          {
            label: 'Beds and wardrobes',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Clothes',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Toys',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Fitted wardrobes',
            color: 'error',
            icon: 'mdi-window-close',
          },
        ],
      },
      {
        title: 'Bathrooms',
        list: [
          {
            label: 'Towels and linen',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Freestanding storage',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Toilets, baths, showers and sinks',
            color: 'error',
            icon: 'mdi-window-close',
          },
          {
            label: 'Heated towel rails',
            color: 'error',
            icon: 'mdi-window-close',
          },
        ],
      },
      {
        title: 'Dining and reception rooms',
        list: [
          {
            label: 'Tables and chairs',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Sofas',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Throws and cushions',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Freestanding lighting and lampshades',
            color: 'success',
            icon: 'mdi-check',
          },
        ],
      },
      {
        title: 'Kitchen and utility rooms',
        list: [
          {
            label: 'Freestanding appliances',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Plates, cutlery, pots and pans',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Kitchen units',
            color: 'error',
            icon: 'mdi-window-close',
          },
          {
            label: 'Fitted/built-in appliances',
            color: 'error',
            icon: 'mdi-window-close',
          },
        ],
      },
      {
        title: 'Garden, garages and outbuildings',
        list: [
          {
            label: 'Tools and lawnmovers',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Children’s toys',
            color: 'success',
            icon: 'mdi-check',
          },
          {
            label: 'Permanent outbuildings',
            color: 'error',
            icon: 'mdi-window-close',
          },
          {
            label: 'Fixed children’s play equipment',
            color: 'error',
            icon: 'mdi-window-close',
          },
        ],
      },
    ];

    return {
      snakeCase,
      mainContentsCalcPoints,
      additionalContentsCalcPoints,
    };
  },
};
</script>

<style lang='scss'>
.contents-calculator > .form-section__container {
  padding-top: 0;

  .form-section__validity {
    display: none;
  }
}
</style>
